import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { buildClassName } from 'utils/build-class-name';
import { homepageIds } from 'constants/test-constants';
import { LOCALE_HOME_PATHS, Namespaces } from 'types/locales';
import styles from './style.module.scss';
import { getAreaThumbnailUrlForMarket } from '../top-markets';

import type { TopMarket } from '../top-markets';

interface CitySelectorProps {
  /** Currently active city name (displayed in the heading). Empty string = "Choose Location" placeholder. */
  activeCityName: string;
  /** List of markets available for selection */
  markets: TopMarket[];
  /** Currently active slug (to highlight in dropdown) */
  activeSlug?: string;
  /** Called when user picks a different city */
  onCityChange: (market: TopMarket) => void;
  /** list: standard dropdown with city names,  grid: two-column pill layout */
  dropdownLayout?: 'list' | 'grid';
  /** Disable city selector dropdown( When disabled dropdown pill shows with no arrow icon and is not clickable) */
  dropdownDisabled?: boolean;
  /** when user has a last searched city, the dropdown should be disabled, and 
   * clicking on the pill/label should redirect to the area page for that location */
  lastSearchedCityUrl? : string;
  onLastSearchCityClick?: () => void;
}

/** Globe SVG shown when no city is selected yet */
function GlobeIcon() {
  return (
    <svg className={styles['globe-icon']} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.4"/>
      <ellipse cx="10" cy="10" rx="4" ry="8.5" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M2 10h16" stroke="currentColor" strokeWidth="1.4"/>
      <path d="M3.5 5.5h13M3.5 14.5h13" stroke="currentColor" strokeWidth="1.2" opacity=".5"/>
    </svg>
  );
}

export default function CitySelector({
  activeCityName,
  markets,
  activeSlug,
  onCityChange,
  dropdownLayout = 'list',
  dropdownDisabled = false,
  lastSearchedCityUrl: cityHref,
  onLastSearchCityClick,
}: CitySelectorProps) {
  const { t } = useTranslation(Namespaces.HOME);
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isPlaceholder = !activeCityName;

  const activeMarket = useMemo(
    () => markets.find(m => m.slug === activeSlug),
    [markets, activeSlug]
  );

  // Close on outside click
  useEffect(() => {
    if (!isOpen || dropdownDisabled) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, dropdownDisabled]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen || dropdownDisabled) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, dropdownDisabled]);

  const handleSelect = useCallback((market: TopMarket) => {
    onCityChange(market);
    setIsOpen(false);
  }, [onCityChange]);

  return (
    <span
      className={buildClassName(
        styles['city-selector'],
        isOpen && styles['is-open'],
        dropdownDisabled && styles['dropdown-disabled']
      )}
      ref={dropdownRef}
    >
      <button
        type="button"
        className={buildClassName(
          styles.trigger,
          isPlaceholder && styles['trigger-placeholder'],
          dropdownDisabled && styles['trigger-dropdown-disabled']
        )}
        onClick={() => {
          if (dropdownDisabled) {
            onLastSearchCityClick?.();
            if (cityHref) router.push(cityHref);
            return;
          }
          setIsOpen(prev => !prev);
        }}
        aria-expanded={dropdownDisabled ? false : isOpen}
        aria-haspopup={dropdownDisabled ? undefined : 'listbox'}
        aria-disabled={dropdownDisabled}
      >
        {/* Circular thumbnail or globe icon */}
        {isPlaceholder ? (
          <span className={styles.thumbnail}>
            <GlobeIcon />
          </span>
        ) : (
          <span className={styles.thumbnail}>
            <img
              src={activeMarket?.thumbnailUrl ?? getAreaThumbnailUrlForMarket({ name: activeCityName })}
              alt=""
            />
          </span>
        )}

        <span className={buildClassName(styles['trigger-name'], isPlaceholder && styles['trigger-name-placeholder'])} data-testid={homepageIds.homesForSaleLocation}>
          {activeCityName || t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_SELECTOR_CHOOSE_LOCATION, { defaultValue: 'Choose Location' })}
        </span>

        {!dropdownDisabled && (
          <svg
            className={buildClassName(styles.chevron, isOpen && styles.open)}
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden
          >
            <path
              d="M2.5 4.5L6 8L9.5 4.5"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>

      {isOpen && !dropdownDisabled && (
        <div
          className={buildClassName(
            styles.dropdown,
            dropdownLayout === 'grid' && styles['dropdown--grid']
          )}
          role="listbox"
        >
          <span className={styles['dropdown-title']}>{t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_SELECTOR_POPULAR_CITIES, { defaultValue: 'Popular Cities' })}</span>
          {dropdownLayout === 'grid' ? (
            <div className={styles['dropdown-grid']}>
              {markets.map(market => (
                <button
                  key={market.slug}
                  type="button"
                  role="option"
                  aria-selected={market.slug === activeSlug}
                  className={buildClassName(
                    styles['grid-option'],
                    market.slug === activeSlug && styles['grid-option-active']
                  )}
                  onClick={() => handleSelect(market)}
                >
                  <span className={styles['grid-option-thumb']}>
                    <img
                      src={market.thumbnailUrl ?? getAreaThumbnailUrlForMarket(market)}
                      alt=""
                    />
                  </span>
                  <span className={styles['grid-option-name']}>{market.name}</span>
                </button>
              ))}
            </div>
          ) : (
            markets.map(market => (
              <button
                key={market.slug}
                type="button"
                role="option"
                aria-selected={market.slug === activeSlug}
                className={buildClassName(
                  styles['dropdown-item'],
                  market.slug === activeSlug && styles.active
                )}
                onClick={() => handleSelect(market)}
              >
                <span className={styles['item-name']}>{market.name}</span>
                {market.listingCount != null && (
                  <span className={styles['item-count']}>
                    {t(LOCALE_HOME_PATHS.LOCAL_FOCUS_CITY_SELECTOR_HOMES, { homeCount: market.listingCount.toLocaleString(), defaultValue: `${market.listingCount.toLocaleString()} homes` })}
                  </span>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </span>
  );
}
